import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, ConflictError } from "@/api/client";
import { useFetchMetrics, useStartRecording } from "@/api/hooks";
import { METRICS_KINDS, type MetricsKind } from "@/domain/metrics-params";
import type { BuildId } from "@/types/identifiers";

const POLL_INTERVAL_MS = 2500;
/** Extra polling time past the nominal duration before giving up — the boat
 * needs a moment to evaluate the metrics and clear the trigger. */
const GRACE_S = 15;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export interface ActiveRecording {
  kind: MetricsKind;
  /** Nominal end of the run (start + duration) — drives the countdown. */
  runEndsAt: number;
}

export function useMetricsActions(buildId: BuildId) {
  const fetchMetrics = useFetchMetrics(buildId);
  const startRecording = useStartRecording(buildId);
  const [recording, setRecording] = useState<ActiveRecording | null>(null);
  // Navigating away abandons the watch; the run still completes boat-side and
  // can be fetched manually afterward. The ref MUST be re-armed in the effect
  // body, not only initialized: StrictMode mounts run cleanup once immediately,
  // and a never-reset ref would permanently kill the poll loop in dev.
  const abandonedRef = useRef(false);
  useEffect(() => {
    abandonedRef.current = false;
    return () => {
      abandonedRef.current = true;
    };
  }, []);

  const handleFetch = (kind: MetricsKind) => {
    fetchMetrics.mutate(kind, {
      onSuccess: () => toast.success(`${METRICS_KINDS[kind].label} recording saved`),
      // "No new run" (values unchanged) is a finding, not a failure.
      onError: (err) =>
        err instanceof ConflictError ? toast.info(err.message) : toast.error(err.message),
    });
  };

  const watchUntilDone = async (kind: MetricsKind, durationS: number) => {
    const runEndsAt = Date.now() + durationS * 1000;
    setRecording({ kind, runEndsAt });
    try {
      while (Date.now() < runEndsAt + GRACE_S * 1000) {
        await sleep(POLL_INTERVAL_MS);
        if (abandonedRef.current) return;
        const result = await api.GET("/api/builds/{build_id}/metrics/{kind}/recording-status", {
          params: { path: { build_id: buildId, kind } },
        });
        // Transient poll errors read as "still recording"; the deadline caps them.
        if (result.data && !result.data.recording) {
          handleFetch(kind);
          return;
        }
      }
      toast.warning(
        `${METRICS_KINDS[kind].label} is still running on the boat — press Start again to keep watching`,
      );
    } finally {
      if (!abandonedRef.current) setRecording(null);
    }
  };

  const handleStart = (kind: MetricsKind, durationS: number) => {
    startRecording.mutate(
      { kind, durationS },
      {
        onSuccess: (data) => {
          void watchUntilDone(kind, data.duration_s ?? durationS);
        },
        onError: (err) => {
          if (err instanceof ConflictError) {
            // Someone else (or a previous tab) triggered it — watch that run.
            toast.info("A recording is already running — watching for it to finish");
            void watchUntilDone(kind, durationS);
          } else {
            toast.error(err.message);
          }
        },
      },
    );
  };

  return {
    handleFetch,
    handleStart,
    fetchingKind: fetchMetrics.isPending ? fetchMetrics.variables : undefined,
    startingKind: startRecording.isPending ? startRecording.variables?.kind : undefined,
    recording,
  };
}
